import React from 'react';
import styled from 'styled-components';
import * as Accordion from '@radix-ui/react-accordion';
import { CgChevronDown } from 'react-icons/cg';

import HTMLWrapper from '../HTMLWrapper';
import documentToReactComponents from '../../util/documentToReactComponents';

import theme from '../../../styles/theme';

const { colors } = theme;

const AccordionContentWrapper = styled.div`
	.accordion__content {
		overflow: hidden;
		font-size: 15px;

		&[data-state='open'] {
			animation: slideDown ${theme.transitionDuration};
		}

		&[data-state='closed'] {
			animation: slideUp ${theme.transitionDuration};
		}

		&-text {
			padding: 0 6px 35px;
			a {
				overflow-wrap: break-word;
				word-wrap: break-word;

				-ms-word-break: break-all;
				word-break: break-all;
				word-break: break-word;

				-ms-hyphens: auto;
				-moz-hyphens: auto;
				-webkit-hyphens: auto;
				hyphens: auto;
			}
		}

		@keyframes slideDown {
			from {
				height: 0;
			}
			to {
				height: var(--radix-accordion-content-height);
			}
		}

		@keyframes slideUp {
			from {
				height: var(--radix-accordion-content-height);
			}
			to {
				height: 0;
			}
		}
	}
`;

const AccordionContent = React.forwardRef(
	({ answer, className, ...props }, forwardedRef) => (
		<AccordionContentWrapper>
			<Accordion.Content
				className="accordion__content"
				{...props}
				ref={forwardedRef}
			>
				{answer?.['childMarkdownRemark'] ? (
					<HTMLWrapper
						html={answer.childMarkdownRemark.html}
						oneColumnList
						discColor="#333"
						className="accordion__content-text"
					/>
				) : (
					<HTMLWrapper
						className="accordion__content-text"
						oneColumnList
						discColor="#333"
					>
						{documentToReactComponents(answer)}
					</HTMLWrapper>
				)}
			</Accordion.Content>
		</AccordionContentWrapper>
	)
);

const AccordionTriggerWrapper = styled(HTMLWrapper)`
	.accordion__trigger {
		width: 100%;
		padding: 0;
		background-color: transparent;
		border: none;

		&[data-state='open'] {
			.accordion__chevron {
				transform: rotate(-90deg);
			}
		}

		&:focus {
			outline: none;
		}
	}

	.accordion__header {
		display: flex;
		padding: ${({ headerPadding }) => headerPadding};
		background-color: transparent;
		flex: 1;
		align-items: center;
		justify-content: space-between;
		text-align: left;
		width: 100%;

		h3 {
			margin-bottom: 0;
		}
	}
	.accordion__chevron {
		transition: transform 300ms cubic-bezier(0.87, 0, 0.13, 1);
	}
`;

const AccordionTrigger = React.forwardRef(
	({ children, className, ...props }, forwardedRef) => (
		<AccordionTriggerWrapper headerPadding={props.headerPadding}>
			<Accordion.Trigger
				className="accordion__trigger"
				{...props}
				ref={forwardedRef}
			>
				<div className="accordion__header">
					<Accordion.Header>{children}</Accordion.Header>
					<CgChevronDown className="accordion__chevron" aria-hidden />
				</div>
			</Accordion.Trigger>
		</AccordionTriggerWrapper>
	)
);

const AccordionWrapper = styled.div`
	.accordion__item {
		overflow: hidden;
		margin-top: 1px;
		border-bottom: 1px solid ${colors.newBrand.lightGrey};
	}

	.accordion__item:first-child {
		margin-top: 0;
	}

	.accordion__item:focus:not(:focus-visible) {
		outline: none;
	}

	.accordion__item:focus-visible {
		position: relative;
		z-index: 1;
		outline: 2px solid ${colors.secondary.main};
	}
`;

const AccordionContentGrid = styled.div`
	display: ${({ icon }) => icon && 'grid'};
	grid-template-columns: ${({ icon }) => icon && 'auto 1fr'};
	gap: ${({ icon }) => icon && '20px'};
`;

const BasicAccordion = ({
	questionSet,
	className,
	defaultFirstOpen,
	openItem,
	setOpenItem,
	headerPadding,
}) => {
	return (
		<AccordionWrapper className={className}>
			<Accordion.Root
				className="accordion"
				type="single"
				collapsible
				defaultValue={defaultFirstOpen && 'item-0'}
				onValueChange={setOpenItem && (value => setOpenItem(value))}
			>
				{questionSet.map((question, index) => {
					return (
						<Accordion.Item
							key={question.questionTitle || question.contentful_id}
							className="accordion__item"
							value={`item-${index}`}
						>
							<AccordionContentGrid icon={question?.icon}>
								{question.icon && <img src={question.icon.file.url} />}
								<div>
									<AccordionTrigger headerPadding={headerPadding}>
										{question.questionTitle || question.title}
									</AccordionTrigger>
									<AccordionContent
										answer={question.answerText || question.richText}
									/>
								</div>
							</AccordionContentGrid>
						</Accordion.Item>
					);
				})}
			</Accordion.Root>
		</AccordionWrapper>
	);
};

export default BasicAccordion;
